import { useFormikContext } from 'formik';

import { SelectField } from '@src/shared/fields/select-field';

import ruleApi from '@src/features/programs/pages/create/services/rule-api';
import { useParams } from 'react-router-dom';
import { RULE_TYPES, type TRule } from '@src/shared/types/rule';
import { OtherRuleRepetitionFields } from './other-rule-repetition-fields';
import { RuleSelect } from './rule-select';
import {
  RuleChoicesMultiSelect,
  type TRuleChoiceOption,
} from './rule-choices-multi-select';
import { SUBMISSION_STATUS } from '@src/shared/types/submission';

// Statuses a submission can be moved to (mirrors STATUSES_ALLOWED in the
// participants stages table), labelled as states rather than actions.
const SUBMISSION_STATUS_OPTIONS: TRuleChoiceOption[] = [
  { value: SUBMISSION_STATUS.inProgress, label: 'In Progress' },
  { value: SUBMISSION_STATUS.submitted, label: 'Under Review' },
  { value: SUBMISSION_STATUS.returned, label: 'Returned' },
  {
    value: SUBMISSION_STATUS.submittedToPartner,
    label: 'Submitted to Partner',
  },
  { value: SUBMISSION_STATUS.approved, label: 'Approved' },
  { value: SUBMISSION_STATUS.withdrawn, label: 'Withdrawn' },
  { value: SUBMISSION_STATUS.archived, label: 'Archived' },
];

type OtherRelatedFormValues = {
  rule?: string;
  rule_choices?: string[];
  rule_type?: string;
};

export const OtherRelatedFields = ({
  hideRepetition = false,
}: {
  hideRepetition?: boolean;
}) => {
  const { id: programId = '' } = useParams();
  const { values, setFieldValue } = useFormikContext<OtherRelatedFormValues>();
  const { data: rulesData } = ruleApi.useFetch({
    program: programId,
    exclude_type: [RULE_TYPES.date],
  });
  const rules = rulesData?.results || [];

  const selectedRule = rules.find(
    (rule) => String(rule.id) === String(values.rule ?? '')
  );
  const choiceOptions = getRuleChoiceOptions(selectedRule);

  return (
    <div className='flex flex-col gap-6'>
      <div className='flex flex-col gap-2'>
        <div className='flex items-center gap-2'>
          <span className='rounded-lg flex items-center justify-center border w-[47px] h-[47px] border-primary-200 bg-primary-200 text-gray-800 cursor-default'>
            if
          </span>
          <div className='grid grid-cols-[2fr_1fr] gap-2 w-full'>
            <RuleSelect
              rules={rules}
              onRuleChange={(ruleId) => {
                const rule = rules.find((item) => String(item.id) === ruleId);
                setFieldValue('rule_type', rule?.type ?? '');
                setFieldValue('rule_choices', []);
              }}
            />
            {choiceOptions ? (
              <RuleChoicesMultiSelect
                options={choiceOptions}
                placeholder={
                  selectedRule?.type === RULE_TYPES.applicationSubmission
                    ? 'Select statuses'
                    : 'Select choices'
                }
              />
            ) : (
              <SelectField
                selectName='is_rule_achieved'
                selectPlaceholder='Select'
                options={[
                  { label: 'Achieved', value: 'ACHIEVED' },
                  {
                    label: 'Not Achieved',
                    value: 'NOT_ACHIEVED',
                  },
                ]}
              />
            )}
          </div>
        </div>
      </div>

      {!hideRepetition && <OtherRuleRepetitionFields />}
    </div>
  );
};

// Rule types whose reminder condition is "one of these values" instead of
// achieved / not achieved. Returns null for the achieved-based types.
const getRuleChoiceOptions = (rule?: TRule): TRuleChoiceOption[] | null => {
  switch (rule?.type) {
    case RULE_TYPES.trueFalse:
      return (rule.choices ?? []).map((choice) => ({
        label: choice,
        value: choice,
      }));
    case RULE_TYPES.applicationSubmission:
      return SUBMISSION_STATUS_OPTIONS;
    default:
      return null;
  }
};
